import { getBezierPath, getSmoothStepPath, getStraightPath, ConnectionLineType } from "reactflow";

export interface Waypoint {
  x: number;
  y: number;
}

export const getEdgePath = (lineType: ConnectionLineType, props: any) => {
  switch (lineType) {
    case ConnectionLineType.Straight:
      return getStraightPath(props);
    case ConnectionLineType.Step:
      return getSmoothStepPath(props);
    default:
      return getBezierPath(props);
  }
};

const bezierPoint = (
  t: number,
  p0: number,
  p1: number,
  p2: number,
  p3: number,
) => {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
};

const getBezierWaypointPath = (
  points: Waypoint[],
  tension: number,
): [string, Waypoint[]] => {
  let d = `M ${points[0].x} ${points[0].y}`;
  const segmentMidpoints: Waypoint[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;

    segmentMidpoints.push({
      x: bezierPoint(0.5, p1.x, cp1x, cp2x, p2.x),
      y: bezierPoint(0.5, p1.y, cp1y, cp2y, p2.y),
    });
  }

  return [d, segmentMidpoints];
};

const getStraightWaypointPath = (points: Waypoint[]): [string, Waypoint[]] => {
  let d = `M ${points[0].x} ${points[0].y}`;
  const segmentMidpoints: Waypoint[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    d += ` L ${p2.x} ${p2.y}`;
    segmentMidpoints.push({
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    });
  }

  return [d, segmentMidpoints];
};

const STEP_BORDER_RADIUS = 5;

const getStepWaypointPath = (points: Waypoint[]): [string, Waypoint[]] => {
  let d = `M ${points[0].x} ${points[0].y}`;
  const segmentMidpoints: Waypoint[] = [];
  const r = STEP_BORDER_RADIUS;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const midX = (p1.x + p2.x) / 2;
    const dy = p2.y - p1.y;
    const absDy = Math.abs(dy);
    const clampedR = Math.min(r, Math.abs(midX - p1.x), absDy / 2);

    if (clampedR > 0 && absDy > 0) {
      const sy = dy > 0 ? 1 : -1;

      d += ` L ${midX - clampedR} ${p1.y}`;
      d += ` Q ${midX} ${p1.y}, ${midX} ${p1.y + sy * clampedR}`;
      d += ` L ${midX} ${p2.y - sy * clampedR}`;
      d += ` Q ${midX} ${p2.y}, ${midX + clampedR} ${p2.y}`;
      d += ` L ${p2.x} ${p2.y}`;
    } else {
      d += ` L ${midX} ${p1.y}`;
      d += ` L ${midX} ${p2.y}`;
      d += ` L ${p2.x} ${p2.y}`;
    }

    segmentMidpoints.push({ x: midX, y: (p1.y + p2.y) / 2 });
  }

  return [d, segmentMidpoints];
};

export const getWaypointPath = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: Waypoint[],
  lineType: ConnectionLineType = ConnectionLineType.Bezier,
  tension = 0.3,
): [string, number, number, Waypoint[]] => {
  const points = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ];

  let d: string;
  let segmentMidpoints: Waypoint[];

  switch (lineType) {
    case ConnectionLineType.Straight:
      [d, segmentMidpoints] = getStraightWaypointPath(points);
      break;
    case ConnectionLineType.Step:
      [d, segmentMidpoints] = getStepWaypointPath(points);
      break;
    default:
      [d, segmentMidpoints] = getBezierWaypointPath(points, tension);
      break;
  }

  const midIdx = Math.floor(points.length / 2);
  const labelX = (points[midIdx - 1].x + points[midIdx].x) / 2;
  const labelY = (points[midIdx - 1].y + points[midIdx].y) / 2;

  return [d, labelX, labelY, segmentMidpoints];
};
